import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import DietRequestDetails from './diet-requests/DietRequestDetails';
import ReportsModal from './diet-requests/ReportsModal';

// Import extracted components and hooks
import DietRequestsFilters from './diet-requests/DietRequestsFilters';
import TablePagination from './common/TablePagination';
import HorizontalTableNavigation from './common/HorizontalTableNavigation';
import useHorizontalScroll from '../hooks/useHorizontalScroll';
import useColumnPinning from '../hooks/useColumnPinning';
import useDietRequestsData from './diet-requests/useDietRequestsData';
import useDietRequestColumns from './diet-requests/useDietRequestColumns';
import useDietRequestsTour from './diet-requests/useDietRequestsTour';
import ErrorMessage from './common/ErrorMessage';

const DietRequests = ({ hasSeenNavbarTour }) => {
  const { isAuthenticated, authCode, userInfo } = useAuth();

  // State for UI
  const [showFilters, setShowFilters] = useState(false);

  // State to show pin icons during tour
  const [showPinIconsForTour, setShowPinIconsForTour] = useState(false);

  // Separate modal states
  const [showDietModal, setShowDietModal] = useState(false);
  const [dietModalData, setDietModalData] = useState(null);

  const [showReportsModal, setShowReportsModal] = useState(false);
  const [reportsModalData, setReportsModalData] = useState(null);

  // Use custom hooks for data, columns, and tour
  const dataHook = useDietRequestsData({ isAuthenticated, authCode });
  const {
    data,
    recommendations,
    totalCount,
    pageCount,
    countries,
    labels,
    profiles,
    loading,
    error,
    pagination,
    setPagination,
    filterInputs,
    handleInputChange,
    applyFilters,
    clearAllFilters,
    parseDateString,
    formatDateForAPI,
    apiResponseData,
    serverFilters
  } = dataHook;

  const handleDownload = (fileType, format) => {
    const params = new URLSearchParams();
    params.append('fileType', fileType);
    params.append('format', format);
    params.append('fromDate', serverFilters.fromDate || '');
    params.append('toDate', serverFilters.toDate || '');
    params.append('profile', serverFilters.profile || '');
    params.append('country', serverFilters.country || '');
    params.append('user', serverFilters.user || '');
    params.append('labels', Array.isArray(serverFilters.labels)
      ? serverFilters.labels.map(l => l.name).join(',') : '');
    params.append('labelQueryCondition', 'OR');
    params.append('test', serverFilters.test ? '1' : '0');
    params.append('recommendationId', serverFilters.recommendationId || '');
    window.location.href = `/downloadDietRequestsData.fxl?${params.toString()}`;
  };

  // Horizontal scroll pagination (must be after data hook since it depends on recommendations)
  const tableContainerRef = useRef(null);
  const {
    horizontalPages,
    currentHorizontalPage,
    showHorizontalPager,
    setShowHorizontalPager,
    canScrollRight,
    scrollToHorizontalPage,
    handleArrowClick
  } = useHorizontalScroll(tableContainerRef, [recommendations]);

  // Column pinning hook
  const {
    columnPinning,
    setColumnPinning,
    getCommonPinningStyles,
  } = useColumnPinning(tableContainerRef, [data]);

  const { columns, getCountryName, pluralize } = useDietRequestColumns({
    countries,
    userInfo,
    setDietModalData,
    setShowDietModal,
    setReportsModalData,
    setShowReportsModal
  });

  const { startTour } = useDietRequestsTour({
    isAuthenticated,
    data,
    showHorizontalPager,
    setShowHorizontalPager,
    horizontalPages,
    hasSeenNavbarTour,
    setShowPinIconsForTour
  });

  // TanStack Table instance
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: pageCount,
    onPaginationChange: setPagination,
    enableColumnPinning: true,
    onColumnPinningChange: setColumnPinning,
    state: {
      pagination,
      columnPinning,
    },
  });

  // Check if user is admin
  const isAdmin = userInfo?.role === 'admin';

  return (
    <div className="bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8" data-intro="page-header">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Diet Requests
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Review and analyse diet requests and responses
              </p>
            </div>
            {/* Tour Button */}
            <button
              onClick={startTour}
              className="hidden md:flex flex-col items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
            >
              <svg
                className="w-5 h-5 mb-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              <span className="text-xs font-medium">Tour</span>
            </button>
          </div>
        </div>

        {/* Error Message Display */}
        {error && (
          <ErrorMessage
            title="Error loading diet requests"
            message={error}
          />
        )}

        <div className="relative">
          {/* Filters Component */}
          <DietRequestsFilters
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            filterInputs={filterInputs}
            handleInputChange={handleInputChange}
            countries={countries}
            labels={labels}
            profiles={profiles}
            isAdmin={isAdmin}
            applyFilters={applyFilters}
            clearAllFilters={clearAllFilters}
            parseDateString={parseDateString}
            formatDateForAPI={formatDateForAPI}
            onDownload={handleDownload}
          />

          {/* Navigation Above Table - Dots and Arrow */}
          <HorizontalTableNavigation
            showHorizontalPager={showHorizontalPager}
            currentHorizontalPage={currentHorizontalPage}
            horizontalPages={horizontalPages}
            scrollToHorizontalPage={scrollToHorizontalPage}
            handleArrowClick={handleArrowClick}
            canScrollRight={canScrollRight}
            position="above"
            variant="both"
          />

          {/* Table and Navigation Container */}
          <div className={`relative border border-gray-200 dark:border-gray-700 overflow-hidden ${showHorizontalPager ? 'border-t-0 sm:rounded-b-lg' : 'sm:rounded-lg'}`}>
            {/* Tour anchor for table - positioned at table top */}
            <div className="absolute top-0 left-0 w-full h-12 pointer-events-none z-10" data-intro="data-table-tour" style={{zIndex: 1}}></div>
            {/* Table */}
            <div ref={tableContainerRef} className={`relative overflow-x-auto ${showHorizontalPager ? 'border-t border-gray-200 dark:border-gray-700' : ''}`} data-intro="data-table">
              <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                <thead className="text-xs text-gray-700 uppercase bg-gray-200 dark:bg-gray-700 dark:text-gray-400" data-intro="data-table-header">
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          scope="col"
                          data-column-id={header.id}
                          data-intro={header.id === 'diets' ? 'result-column' : undefined}
                          className={`px-6 py-3 whitespace-nowrap group relative ${
                            header.column.getIsPinned() === 'left'
                              ? 'bg-gray-200 dark:bg-gray-700'
                              : ''
                          }`}
                          style={getCommonPinningStyles(header.column, table)}
                        >
                          <div className="flex items-center">
                            <span>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>

                            {/* Pin Icon - always visible for pinned columns, appears on hover for unpinned */}
                            <button
                              onClick={() => {
                                const isPinned = header.column.getIsPinned();
                                if (isPinned === 'left') {
                                  header.column.pin(false); // Unpin
                                } else {
                                  header.column.pin('left'); // Pin to left
                                }
                              }}
                              className={`hidden md:block ml-2 p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none transition-opacity ${
                                header.column.getIsPinned() === 'left' || showPinIconsForTour
                                  ? 'opacity-100'
                                  : 'opacity-0 group-hover:opacity-100'
                              }`}
                              data-intro={headerGroup.headers.indexOf(header) === 0 ? "pin-icon" : undefined}
                              title={header.column.getIsPinned() === 'left' ? 'Unpin column' : 'Pin column to left'}
                            >
                              <svg
                                className={`w-4 h-4 ${
                                  header.column.getIsPinned() === 'left' || showPinIconsForTour
                                    ? 'text-blue-600 dark:text-blue-400'
                                    : 'text-gray-400 dark:text-gray-500'
                                }`}
                                fill="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12M8.8,14L10,12.8V4H14V12.8L15.2,14H8.8Z"/>
                              </svg>
                            </button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No recommendations found
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map(row => (
                      <tr key={row.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                        {row.getVisibleCells().map((cell, cellIndex) => (
                          <td
                            key={cell.id}
                            className={`px-6 py-4 whitespace-nowrap ${cellIndex === 0 ? 'font-medium text-gray-900 dark:text-white' : ''} ${
                              cell.column.getIsPinned() === 'left'
                                ? 'bg-white dark:bg-gray-800'
                                : ''
                            }`}
                            title={String(cell.getValue() || '')}
                            style={getCommonPinningStyles(cell.column, table)}
                          >
                            <span className="block max-w-xs overflow-hidden overflow-ellipsis">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Navigation and Pagination Container */}
            <div className="bg-gray-50/50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
              {/* Horizontal Navigation - Dots and Arrow Below Table */}
              <HorizontalTableNavigation
                showHorizontalPager={showHorizontalPager}
                currentHorizontalPage={currentHorizontalPage}
                horizontalPages={horizontalPages}
                scrollToHorizontalPage={scrollToHorizontalPage}
                handleArrowClick={handleArrowClick}
                canScrollRight={canScrollRight}
                position="below"
                variant="both"
              />

              {/* Table Pagination Component */}
              <div className="px-2 pb-2">
                <div className="flex items-center justify-between mt-2" data-intro="pagination">
                  <TablePagination
                    table={table}
                    totalCount={totalCount}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Loading Overlay */}
          {loading && (
            <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
          )}

          {/* Diet Request Details Modal */}
          <DietRequestDetails
            showModal={showDietModal}
            onClose={() => setShowDietModal(false)}
            modalData={dietModalData}
            countries={countries}
            isAdmin={isAdmin}
            getCountryName={getCountryName}
            pluralize={pluralize}
            envName={apiResponseData?.envName}
          />

          {/* Reports Modal */}
          <ReportsModal
            showModal={showReportsModal}
            onClose={() => setShowReportsModal(false)}
            reports={reportsModalData?.reports}
            modalTitle={reportsModalData?.modalTitle}
          />
        </div>
      </div>
    </div>
  );
};

export default DietRequests;
